// Full review queue for the current store. Mirrors the old Home "top of stack"
// card but shows every row and adds filter chips (all / fast / flagged) and an
// age-in-queue column. Sort is fixed to oldest-first (the review work order).

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "../lib/api";
import type { QueueRow } from "@shared/types";

const STORAGE_STORE_KEY = "smartrx-entry.currentStoreCode";

function getCurrentStoreCode(): string {
  return (typeof window !== "undefined" && window.localStorage.getItem(STORAGE_STORE_KEY)) || "GV";
}

type FilterKey = "all" | "fast" | "flagged";

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "all",     label: "All" },
  { key: "fast",    label: "Fast path" },
  { key: "flagged", label: "Flagged" },
];

export function Queue() {
  const [rows, setRows] = useState<QueueRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterKey>("all");
  const storeCode = getCurrentStoreCode();

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    apiGet<QueueRow[]>(`/queue?store=${storeCode}&filter=${filter}&limit=200`, ctrl.signal)
      .then((data) => { setRows(data); setError(null); })
      .catch((e) => { if (!ctrl.signal.aborted) { setRows([]); setError(String(e)); } })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
    return () => ctrl.abort();
  }, [filter, storeCode]);

  const stats = useMemo(() => {
    const flagged = rows.filter(r => (r.rxConfidence ?? 0) < 0.85 || r.isControlled || r.isCompound).length;
    return { total: rows.length, flagged, fast: rows.length - flagged };
  }, [rows]);

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-baseline justify-between mb-1">
        <h2 className="text-2xl font-semibold">Review queue</h2>
        <span className="text-ink-soft text-sm">{storeCode}</span>
      </div>
      <p className="text-ink-soft mb-6">
        {error
          ? <span className="text-bad">Queue unavailable: {error}</span>
          : `${stats.total} prescription${stats.total === 1 ? "" : "s"} waiting — ${stats.fast} fast path, ${stats.flagged} flagged. Oldest first.`}
      </p>

      <div className="flex gap-1 mb-4">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded text-sm border ${
              filter === f.key
                ? "bg-accent text-white border-accent"
                : "bg-surface text-ink-soft border-border hover:bg-pill"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-surface border border-border rounded-lg overflow-hidden">
        <div className="grid grid-cols-[64px_1fr_1fr_90px_90px_auto_88px] gap-3 px-4 py-2 text-xs uppercase tracking-wide text-ink-soft border-b border-border bg-pill">
          <span>#</span>
          <span>Patient</span>
          <span>Drug</span>
          <span>Age</span>
          <span>Score</span>
          <span>Flags</span>
          <span></span>
        </div>
        {loading && rows.length === 0 && (
          <p className="text-ink-soft text-sm py-6 text-center">Loading…</p>
        )}
        {!loading && rows.length === 0 && !error && (
          <p className="text-ink-soft text-sm py-10 text-center">
            Queue is empty for {storeCode}. New uploads and Updox faxes will appear here.
          </p>
        )}
        <div className="divide-y divide-border">
          {rows.map((r) => (
            <Link
              key={r.rxId}
              to={`/review/${r.rxId}`}
              className="grid grid-cols-[64px_1fr_1fr_90px_90px_auto_88px] gap-3 px-4 py-2.5 items-center text-sm hover:bg-pill"
            >
              <span className="text-ink-soft">#{r.rxId}</span>
              <span className="truncate"><strong>{r.patientName ?? "—"}</strong></span>
              <span className="truncate">{r.drug}</span>
              <span className="text-ink-soft">{ageInQueue(r.receivedAt)}</span>
              <span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${confClass(r.rxConfidence)}`}>
                  {r.rxConfidence?.toFixed(2) ?? "—"}
                </span>
              </span>
              <span className="flex gap-1 flex-wrap">
                {r.isControlled && <span className="text-[10px] px-1.5 py-0.5 rounded bg-warn-soft text-warn">controlled</span>}
                {r.isCompound && <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent-soft text-accent">compound</span>}
                {r.species !== "human" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#ecf6e7] text-[#4d7626]">vet</span>}
                {r.warningCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-bad-soft text-bad">{r.warningCount} warn</span>}
              </span>
              <span className="text-right">
                <span className="px-3 py-1 rounded bg-accent text-white text-xs font-medium">Review</span>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function ageInQueue(receivedAt: string): string {
  const t = new Date(receivedAt).getTime();
  if (Number.isNaN(t)) return "—";
  const min = Math.max(0, Math.floor((Date.now() - t) / 60000));
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 48) return `${hr}h`;
  return `${Math.floor(hr / 24)}d`;
}

function confClass(c: number | null): string {
  if (c === null) return "bg-pill text-ink-soft";
  if (c >= 0.85) return "bg-good-soft text-good";
  if (c >= 0.6)  return "bg-warn-soft text-warn";
  return "bg-bad-soft text-bad";
}
